// Sources:
// https://github.com/ueberdosis/tiptap/issues/1036#issuecomment-981094752
// https://github.com/django-tiptap/django_tiptap/blob/main/django_tiptap/templates/forms/tiptap_textarea.html#L453-L602

import { Extension } from '@tiptap/core';
import { AllSelection, TextSelection } from 'prosemirror-state';

const clamp = (val, min, max) => {
  if (val < min) {
    return min;
  }

  if (val > max) {
    return max;
  }

  return val;
};

const IndentProps = {
  min: 0,
  max: 210,

  more: 30,
  less: -30,
};

function isBulletListNode(node) {
  return node.type.name === 'bullet_list';
}

function isOrderedListNode(node) {
  return node.type.name === 'order_list';
}

function isTodoListNode(node) {
  return node.type.name === 'todo_list';
}

function isListNode(node) {
  return isBulletListNode(node)
    || isOrderedListNode(node)
    || isTodoListNode(node);
}

function setNodeIndentMarkup(tr, pos, delta) {
  if (!tr.doc) {
    return tr;
  }

  const node = tr.doc.nodeAt(pos);
  if (!node) {
    return tr;
  }

  const minIndent = IndentProps.min;
  const maxIndent = IndentProps.max;

  const indent = clamp(
    (node.attrs.indent || 0) + delta,
    minIndent,
    maxIndent,
  );

  if (indent === node.attrs.indent) {
    return tr;
  }

  const nodeAttrs = {
    ...node.attrs,
    indent,
  };

  return tr.setNodeMarkup(pos, node.type, nodeAttrs, node.marks);
}

const updateIndentLevel = (tr, delta) => {
  const { doc, selection } = tr;

  if (!doc || !selection) return tr;

  if (!(selection instanceof TextSelection || selection instanceof AllSelection)) {
    return tr;
  }

  const { from, to } = selection;

  doc.nodesBetween(from, to, (node, pos) => {
    const nodeType = node.type;

    if (nodeType.name === 'paragraph' || nodeType.name === 'heading') {
      // eslint-disable-next-line no-param-reassign
      tr = setNodeIndentMarkup(tr, pos, delta);
      return false;
    }

    if (isListNode(node)) {
      return false;
    }

    return true;
  });

  return tr;
};

const Indent = Extension.create({
  name: 'indent',

  addOptions() {
    return {
      types: ['heading', 'paragraph'],
      indentLevels: [0, 30, 60, 90, 120, 150, 180, 210],
      defaultIndentLevel: 0,
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: this.options.defaultIndentLevel,
            renderHTML: (attributes) => {
              if (attributes.indent === 0) {
                return {};
              }

              return {
                style: `margin-left: ${attributes.indent}px!important;`,
              };
            },
            parseHTML: (element) => parseInt(element.style.marginLeft, 10) || this.options.defaultIndentLevel,
          },
        },
      },
    ];
  },

  addCommands() {
    /* eslint-disable no-param-reassign, no-unused-expressions */
    return {
      indent: () => ({ tr, state, dispatch, editor }) => {
        const { selection } = state;
        tr = tr.setSelection(selection);
        tr = updateIndentLevel(tr, IndentProps.more);

        editor.chain().focus().run();

        if (tr.docChanged) {
          dispatch && dispatch(tr);
          return true;
        }

        return false;
      },
      outdent: () => ({ tr, state, dispatch, editor }) => {
        const { selection } = state;
        tr = tr.setSelection(selection);
        tr = updateIndentLevel(tr, IndentProps.less);

        editor.chain().focus().run();

        if (tr.docChanged) {
          dispatch && dispatch(tr);
          return true;
        }

        return false;
      },
    };
    /* eslint-enable no-param-reassign, no-unused-expressions */
  },
  addKeyboardShortcuts() {
    const isInList = () => this.editor.isActive('bulletList')
      || this.editor.isActive('orderedList')
      || this.editor.isActive('taskList');

    return {
      Tab: () => {
        if (!isInList()) {
          return this.editor.commands.indent();
        }
      },
      'Shift-Tab': () => {
        if (!isInList()) {
          return this.editor.commands.outdent();
        }
      },
      Backspace: () => {
        if (!isInList()) {
          return this.editor.commands.outdent();
        }
      },
    };
  },
});

export default Indent;
